import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ChevronRight, Globe, Shield, Layers, CheckCircle, FileText, Smartphone, Bell, Menu, X, ChevronDown, Moon, MapPin, Clock, Wifi, Database, Youtube, FacebookIcon, InstagramIcon } from 'lucide-react';
import { auth, db } from './firebase';
import Additional from './Additional';  
import PCIDSS from "./assets/pci-dss.png"
import BusinessAdmin from './components/BusinessAdmin';
import IOSGuide from './components/iOSGuide';
import AndroidGuide from './components/AndroidGuide';
import Post from './components/Post';
import AppleLogo from "./assets/apple.png"
import CachedImg from "./components/CachedImg"; 
import PlatformInstallation from './components/iOSnAndroid';
import { useLocalStorageImage } from "./components/useLocalStorageImage";
import RegionalPlansSection from './components/RegionalPlansSection';
import RegionalPlansPage from './RegionalPlansPage';
import AndroidLogo from "./assets/play_store.png"
import AirvoyAPIDocs from './components/Docs';
import { onAuthStateChanged } from 'firebase/auth';
import CountryDetails from './components/CountryDetails';
import { collection, onSnapshot } from 'firebase/firestore';
import { query, orderBy, getDocs } from 'firebase/firestore';
import AirvoyActivateAndHow from './components/Activate';
import Dashboard from './Dashboard';
import Profile from './Profile';
import Browse from './Browse';
import AuthModal from './components/AuthModal';
import QRCode from "./assets/qrcode.png" 
import Internet from "./assets/internet.png"
import WorldWide from "./assets/worldwide.png";
import Checkout from './Checkout';
import AboutUs from './Aboutus';
import Support from './Support';
import TermsOfService from './TermsOfService';
import Faq from './Faq';
import Installation from './Installation';
import { Link } from 'react-router-dom';
import PrivacyPolicy from './PrivacyPolicy';
import { FaTiktok } from "react-icons/fa";
import FullLogo from './assets/Airvoylogo-dark-two.png';
import LogoClear from './assets/airvoy_wb.png';
import Partnerships from './components/Partnerships';
import { LanguageProvider, useTranslation } from './useTranslation';
import LanguageSelector from './LanguageSelector';
import Startups from './components/startups';


function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth", // 👈 smooth scrolling
    });
  }, [pathname]);

  return null;
}

function HeroPhone() {

  return (
    <div className="flex justify-center">
      <img
        src="https://maresone.s3.us-east-1.amazonaws.com/airvoy/heroimage.png"
        alt="Airvoy eSIM"
        className="w-96 h-auto rounded-lg object-cover drop-shadow-2xl"
        loading="lazy"
        decoding="async"
      />
    </div>
  );
}

const TYPOGRAPHY = {
  xs: 'text-xs',      // 12px
  sm: 'text-sm',      // 14px
  base: 'text-base',  // 16px
  lg: 'text-lg',      // 18px
  xl: 'text-xl',      // 20px
  '2xl': 'text-2xl',  // 24px
  '3xl': 'text-3xl',  // 30px
  '4xl': 'text-4xl',  // 36px
  '5xl': 'text-5xl',  // 48px
  '6xl': 'text-6xl',  // 60px
};

const COLORS = {
  // Brand colors using modern OKLCH-based approach
  primary: {
    50: 'bg-purple-50',
    100: 'bg-purple-100',
    500: 'bg-[#7B3EF0]',
    600: 'bg-[#6b33e6]',
    700: 'bg-[#5d2bc4]',
  },
  // Semantic text colors with better contrast
  text: {
    primary: 'text-gray-900',
    secondary: 'text-gray-600',
    muted: 'text-gray-500',
    inverse: 'text-white',
    accent: 'text-purple-600',
  },
  // Background system with semantic naming
  background: {
    primary: 'bg-white',
    secondary: 'bg-gray-50',
    tertiary: 'bg-gray-100',
    accent: 'bg-gradient-to-br from-purple-50 to-indigo-100',
    dark: 'bg-[#1a103c]',
    surface: 'bg-white',
  },
  // Interactive states
  interactive: {
    default: 'bg-[#7B3EF0]',
    hover: 'hover:bg-[#6b33e6]',
    active: 'active:bg-[#5d2bc4]',
    disabled: 'bg-gray-300',
  },
  // Status colors
  status: {
    success: 'bg-green-500',
    warning: 'bg-yellow-500',
    error: 'bg-red-500',
    info: 'bg-blue-500',
  },
  // Border system
  border: {
    default: 'border-gray-200',
    light: 'border-gray-100',
    dark: 'border-gray-300',
    accent: 'border-purple-200',
  }
};

// Theme Context
const ThemeContext = React.createContext();


const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('light'); // Changed from 'system' to 'light'
  const [resolvedTheme, setResolvedTheme] = useState('light');

  useEffect(() => {
    // Always set to light theme - removed system detection
    setResolvedTheme('light');
  }, [theme]);

  useEffect(() => {
    // Always remove dark class to ensure light theme
    document.documentElement.classList.remove('dark');
  }, [resolvedTheme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Navigation Component + translations
const Navigation = ({ user, showAuthModal, setShowAuthModal }) => {
  const { t } = useTranslation(); // Add this line
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showAppPopup, setShowAppPopup] = useState(false);
  const location = useLocation();
  
  const isHomePage = location.pathname === '/';

  const handleSignOut = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error("Sign out error:", error);
    }
  };

  return (
    <nav className={`${isHomePage ? 'bg-[#1a103c]' : 'bg-white shadow-sm'} border-b ${isHomePage ? 'border-white/10' : 'border-gray-200'}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src={isHomePage ? FullLogo : LogoClear} 
              alt="Airvoy Logo"
              className="h-9 w-auto"
            />
          </Link>

          <div className="hidden md:flex items-center gap-2">
            {[
              { href: "/installation", label: t('nav.installEsim') }, // Changed
              { href: "/admin", label: t('nav.admin') }, // Changed
              { href: "/browse", label: t('nav.browsePlans') }, // Changed
              { href: "/partnerships", label: t('nav.partnerships') }, // Changed
              ...(user ? [{ href: "/dashboard", label: t('nav.dashboard') }] : []), // Changed
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  isHomePage 
                    ? 'text-white hover:bg-white/10' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                {link.label}
              </a>
            ))}

            {/* Add Language Selector */}
            <LanguageSelector />

            {user ? (
              <button
                onClick={handleSignOut}
                className="ml-2 px-4 py-2 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 transition"
              >
                {t('nav.signOut')} {/* Changed */}
              </button>
            ) : (
              <button
                onClick={() => setShowAuthModal(true)}
                className={`ml-2 px-4 py-2 text-sm font-medium rounded-lg transition ${
                  isHomePage 
                    ? 'border border-white/15 text-white hover:bg-white/10' 
                    : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {t('nav.signIn')} {/* Changed */}
              </button>
            )}
 
            <button
              type="button"
              onClick={() => setShowAppPopup(true)}
              className="ml-4 inline-flex items-center gap-3 rounded-full bg-[#7B3EF0] hover:bg-[#6b33e6] text-white px-6 py-3 text-sm font-semibold shadow-lg transition"
              aria-haspopup="dialog"
              aria-expanded={showAppPopup}
            >
              <span>{t('nav.getTheApp')}</span> {/* Changed */}
            </button>
          </div>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`md:hidden p-2 rounded-lg transition ${
              isHomePage 
                ? 'text-white/90 hover:bg-white/10' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile nav - update text here too */}
      {isMenuOpen && (
        <div className={`md:hidden border-t ${isHomePage ? 'border-white/10' : 'border-gray-200'}`}>
          <div className={`px-6 py-4 space-y-2 ${isHomePage ? 'bg-[#1a103c]' : 'bg-white'}`}>
            {[
              { href: "/installation", label: t('nav.installEsim') }, // Changed
              { href: "/faq", label: t('nav.faqs') }, // Changed
              { href: "/partnerships", label: t('nav.partnerships') }, // Changed
              { href: "/browse", label: t('nav.browsePlans') }, // Changed
              ...(user ? [{ href: "/dashboard", label: t('nav.dashboard') }] : []), // Changed
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`block px-4 py-3 rounded-lg text-base font-medium transition ${
                  isHomePage 
                    ? 'text-white/80 hover:text-white hover:bg-white/10' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                {link.label}
              </a>
            ))}
            <div className="px-4 py-3">
              <LanguageSelector />
            </div>
            <div className={`pt-3 border-t flex items-center gap-3 ${isHomePage ? 'border-white/10' : 'border-gray-200'}`}>
              {user ? (
                <button
                  onClick={handleSignOut}
                  className="flex-1 px-4 py-3 text-base font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                >
                  {t('nav.signOut')} {/* Changed */}
                </button>
              ) : (
                <button
                  onClick={() => setShowAuthModal(true)}
                  className={`flex-1 px-4 py-3 text-base font-medium rounded-lg transition ${
                    isHomePage 
                      ? 'border border-white/15 text-white hover:bg-white/10' 
                      : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {t('nav.signIn')} {/* Changed */}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* App popup modal - update text */}
      {showAppPopup && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        >
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowAppPopup(false)}
          />

          <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('nav.getTheApp')}</h3> {/* Changed */}
              <button
                onClick={() => setShowAppPopup(false)}
                className="p-2 rounded-md hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            <div className="space-y-3">
              <a
                target='_blank'
                href="https://apps.apple.com/us/app/airvoy-esim/id6751522296"
                className="flex items-center justify-between rounded-xl border border-gray-200 p-4 hover:bg-gray-50 transition"
              >
                <div className="flex items-center gap-3">
                  <img src={AppleLogo} alt="iOS" className="h-12 w-auto" />
                  <div>
                    <div className="font-medium text-gray-900">iOS</div>
                    <div className="text-sm text-gray-500">Download from {t('hero.appStore')}</div> {/* Changed */}
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </a>
              <a
                target='_blank'
                href="https://play.google.com/store/apps/details?id=com.airvoy.airvoy" 
                className="flex items-center justify-between rounded-xl border border-gray-200 p-4 hover:bg-gray-50 transition"
              >
                <div className="flex items-center gap-3">
                  <img src={AndroidLogo} alt="Android" className="h-12 w-auto" />
                  <div>
                    <div className="font-medium text-gray-900">Android</div>
                    <div className="text-sm text-gray-500">Download from {t('hero.googlePlay')}</div> {/* Changed */}
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </a>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowAppPopup(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};


// Hero Section with Search Functionality
const HeroSection = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const { t, language } = useTranslation();
  const isRTL = language === 'ar';

  const searchCountriesInFirebase = async (searchTerm) => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      setShowDropdown(false);
      return;
    }

    try {
      setIsSearching(true);
      
      // Search by country name (case insensitive)
      const nameQuery = query(
        collection(db, 'countries'),
        orderBy('name')
      );
      
      const snapshot = await getDocs(nameQuery);
      const allCountries = snapshot.docs.map(doc => ({
        iso: doc.id,
        ...doc.data()
      }));

      // Filter results client-side for more flexible search
      const filtered = allCountries.filter(country =>
        country.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.iso?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.region?.toLowerCase().includes(searchTerm.toLowerCase())
      ).slice(0, 8); // Limit to 8 results for dropdown

      setSearchResults(filtered);
      setShowDropdown(filtered.length > 0);
      setIsSearching(false);
    } catch (error) {
      console.error('Error searching countries:', error);
      setIsSearching(false);
      setShowDropdown(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.length > 0) {
        searchCountriesInFirebase(searchTerm);
      } else {
        setSearchResults([]);
        setIsSearching(false);
        setShowDropdown(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Handle country selection
  const handleCountrySelect = (country) => {
    setSearchTerm('');
    setShowDropdown(false);
    setSearchResults([]);
    // Navigate to browse page with selected country
    window.location.href = `/country/${country.iso}`;
  };

  // Handle search input
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchResults.length > 0) {
      handleCountrySelect(searchResults[0]);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.search-container')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <section className="relative isolate overflow-hidden bg-[#120a2a]">
      <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-purple-500/20 blur-3xl"></div>
      <div className="pointer-events-none absolute -bottom-32 -right-24 h-80 w-80 rounded-full bg-indigo-500/20 blur-3xl"></div>

      <div className="py-20 lg:pt-12 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 lg:gap-16 items-center">
            <div className="space-y-8">
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 text-xs font-medium rounded-full bg-white/10 text-white/90 ring-1 ring-white/15 backdrop-blur">
                <span className="inline-block h-2 w-2 rounded-full bg-purple-300"></span>
                {t('hero.badge')} {/* Changed */}
              </span>
 
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight">
                {t('hero.title')} <span className="text-purple-300 font-bold">{t('hero.titleHighlight')}</span> {/* Changed */}
              </h1>

              <p className="text-lg sm:text-xl leading-relaxed text-white/80 max-w-xl">
                {t('hero.subtitle')} {/* Changed */}
              </p>

              <div className="relative max-w-md search-container">
                <form onSubmit={handleSearchSubmit} className="relative">
                  <label htmlFor="destination" className="sr-only">Search destination</label>
                  <div className="group mb-16 sm:mb-4 relative">
                    <input
                      id="destination"
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder={t('hero.searchPlaceholder')} 
                      className="w-full px-5 sm:px-6 py-3.5 sm:py-4 rounded-2xl bg-white/10 text-white placeholder-white/60 shadow-lg ring-1 ring-white/15 focus:outline-none focus:ring-2 focus:ring-purple-400/70 backdrop-blur-md"
                    />
                    <button
                      type="submit"
                      aria-label="Search destination"
                      className="absolute right-1.5 top-1.5 bottom-1.5 px-4 sm:px-5 rounded-xl bg-[#7B3EF0] hover:bg-[#6b33e6] text-white font-medium transition focus:outline-none focus:ring-2 focus:ring-white/40"
                    >
                      {isSearching ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/50 border-t-transparent mx-0.5" />
                      ) : (
                        <span className="text-lg leading-none">→</span>
                      )}
                    </button>
                  </div>
                </form>

                <div className="mt-4 hidden sm:flex flex-wrap gap-3">
                  <a
                    href="https://apps.apple.com/us/app/airvoy-esim/id6751522296"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-white/15 bg-white/5 text-white/90 hover:bg-white/10 transition backdrop-blur"
                  >
                    <img src={AppleLogo} alt="App Store" className="h-5 w-auto" />
                    <span className="text-sm font-medium">{t('hero.appStore')}</span> {/* Changed */}
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.airvoy.airvoy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-white/15 bg-white/5 text-white/90 hover:bg-white/10 transition backdrop-blur"
                  >
                    <img src={AndroidLogo} alt="Google Play" className="h-5 w-auto" />
                    <span className="text-sm font-medium">{t('hero.googlePlay')}</span> {/* Changed */}
                  </a>
                </div>

                {/* Search Dropdown - Update search results display */}
                {showDropdown && (
                  <div className={`absolute top-full ${isRTL ? 'right-0' : 'left-0'} ${isRTL ? 'left-0' : 'right-0'} mt-2 z-[60]`}>
                    <div className="overflow-hidden rounded-2xl border border-gray-200/70 bg-white shadow-xl max-h-80 overflow-y-auto">
                      {searchResults.map((country) => (
                        <button
                          key={country.iso}
                          type="button"
                          onClick={() => handleCountrySelect(country)}
                          className={`w-full flex items-center p-3.5 hover:bg-gray-50 transition border-b border-gray-100 last:border-b-0 ${isRTL ? 'text-right flex-row-reverse' : 'text-left'}`}
                        >
                          {country.image_url ? (
                            <img
                              src={country.image_url}
                              alt={country.name}
                              className={`w-9 h-9 rounded-full object-cover ${isRTL ? 'ml-3' : 'mr-3'}`}
                              loading="lazy"
                              decoding="async"
                            />
                          ) : (
                            <div className={`w-9 h-9 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 ${isRTL ? 'ml-3' : 'mr-3'} flex items-center justify-center`}>
                              <span className="text-white text-xs font-bold">{country.iso}</span>
                            </div>
                          )}
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{country.name}</div>
                            {country.region && (
                              <div className="text-xs text-gray-500">{country.region}</div>
                            )}
                          </div>
                          <div className="text-xs text-gray-400">{country.iso}</div>
                        </button>
                      ))}

                      {searchResults.length === 0 && searchTerm && !isSearching && (
                        <div className="p-4 text-center text-gray-500">
                          {t('common.noResults')} "{searchTerm}"
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

        {/* Right */}
        <div className="relative">
          {/* Fixed height container to prevent layout shift */}
          <div className="relative h-96 sm:h-[28rem] lg:h-[36rem] flex items-center justify-center">
            {/* subtle frame behind the phone */}
            <div className="absolute -inset-6 -z-10 rounded-3xl bg-gradient-to-tr from-purple-500/20 to-indigo-500/20 blur-2xl" />
            <HeroPhone />
          </div>
        </div>
      </div>
    </div>
  </div>

  {/* mobile-only badges row */}
     <div className="mt-2 pb-10 sm:hidden">
        <div className="text-center mb-4">
          <p className="text-white/70 text-[10px] tracking-widest uppercase font-semibold">Available on</p>
        </div>
        <div className="flex flex-col space-y-3 max-w-48 mx-auto">
          <a
            href="https://play.google.com/store/apps/details?id=com.airvoy.airvoy"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Get it on Google Play"
            className="flex items-center justify-center rounded-lg px-4 py-3 bg-white/10 hover:bg-white/15 backdrop-blur transition gap-3"
          >
            <img
              src={AndroidLogo}
              alt="Get it on Google Play"
              className="h-6 w-auto"
              loading="lazy"
              decoding="async"
            />
            <span className="text-white text-sm font-medium">{t('hero.googlePlay')}</span> {/* Changed */}
          </a>
          <a
            href="https://apps.apple.com/us/app/airvoy-esim/id6751522296"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Get it on the App Store"
            className="flex items-center justify-center rounded-lg px-4 py-3 bg-white/10 hover:bg-white/15 backdrop-blur transition gap-3"
          >
            <img
              src={AppleLogo}
              alt="Get it on the App Store"
              className="h-6 w-auto"
              loading="lazy"
              decoding="async"
            />
            <span className="text-white text-sm font-medium">{t('hero.appStore')}</span> {/* Changed */}
          </a>
        </div>
      </div>
</section>

  );
};


// CountryPriceCard Component with original UI + translations
const CountryPriceCard = ({ country, handleCountrySelect, onBuyClick, loading }) => {
  const { t } = useTranslation(); // Only add this line

  // Loading skeleton - keep exactly the same
  if (loading) {
    return (
      <div className={`${COLORS.background.primary} relative rounded-3xl border border-gray-200/70 shadow-sm overflow-hidden`}>
        <div className="h-40 bg-gradient-to-r from-gray-100 to-gray-50" />
        <div className="p-6 space-y-4">
          <div className="h-6 w-40 bg-gray-200 rounded-md" />
          <div className="h-4 w-56 bg-gray-200 rounded-md" />
          <div className="h-11 w-full bg-gray-200 rounded-lg" />
        </div>
        <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.6s_infinite] bg-[linear-gradient(90deg,transparent,rgba(255,255,255,.6),transparent)]" />
      </div>
    );
  }

  // Empty state - keep exactly the same except translate text
  if (!country) {
    return (
      <div className={`${COLORS.background.primary} border border-gray-200 rounded-3xl shadow-sm p-10 text-center ${COLORS.text.muted}`}>
        <Globe className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p className={TYPOGRAPHY.base}>{t('popularCountries.noPlansAvailable')}</p>
      </div>
    );
  }

  const getUnlimitedOneDayPlan = () => {
    if (country.unlimited_plans?.['1']?.length > 0) {
      return { ...country.unlimited_plans['1'][0], duration: 1, type: 'unlimited' };
    }
    return null;
  };

  const plan = getUnlimitedOneDayPlan();
  const price = plan
    ? (plan.price * (1 + (country.markup_percentage || 25) / 100)).toFixed(2)
    : null;

  return (
    <div
      onClick={() => handleCountrySelect(country)}
      className={[
        COLORS.background.primary,
        'relative cursor-pointer rounded-3xl border border-gray-200/80 overflow-hidden',
        'shadow-sm hover:shadow-xl hover:border-gray-300/80',
        'transition-all duration-300 will-change-transform group'
      ].join(' ')}
    >
      {/* Media header - keep exactly the same */}
      <div className="relative h-44 sm:h-48 overflow-hidden">
        {/* gradient base so it looks good even while image loads */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600" />
        {country.image_url && (
          <CachedImg
            src={country.image_url}
            alt={country.name}
            className="absolute inset-0 h-full w-full object-cover opacity-70 transition-transform duration-500 group-hover:scale-[1.03]"
            keyPrefix="airvoy:countries"
            ttlDays={14}
          />
        )}

        {/* subtle vignette & bottom fade for legibility */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_40%,rgba(0,0,0,.35))]" />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/45 to-transparent" />

        {/* country name */}
        <div className="absolute bottom-0 left-0 p-6">
          <h3 className={`${TYPOGRAPHY.xl} font-bold text-white drop-shadow-sm`}>
            {country.name}
          </h3>
        </div>

        {/* price badge (if plan) */}
        {plan && (
          <div className="absolute top-3 right-3">
            <div className="rounded-2xl bg-white/90 backdrop-blur px-3 py-1.5 shadow-md border border-white">
              <span className="text-sm font-semibold text-gray-900">
                {t('popularCountries.fromPrice')} ${price}{t('popularCountries.perDay')}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6 space-y-4">
        {plan ? (
          <>
            <div className="flex items-center justify-between">
              <span className={`inline-flex items-center gap-2 ${COLORS.text.primary} ${TYPOGRAPHY.base}`}>
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-green-50 ring-1 ring-green-100">
                  <Wifi className="w-4 h-4 text-green-600" />
                </span>
                <span className="font-medium">{t('popularCountries.unlimited')}</span>
              </span>

              <span className={`${TYPOGRAPHY['2xl']} font-bold text-gray-900`}>
                ${price}
              </span>
            </div>

            <p className={`${TYPOGRAPHY.sm} ${COLORS.text.muted}`}>
              {t('popularCountries.startingAt')} ${price}{t('popularCountries.perDay')}
            </p>

            <button
              // NOTE: to avoid navigating when clicking the button, you can uncomment:
              // onClick={(e) => { e.stopPropagation(); onBuyClick(country); }}
              onClick={() => onBuyClick(country)}
              className={[
                'w-full inline-flex items-center justify-center',
                'py-3 rounded-xl font-semibold',
                `${TYPOGRAPHY.base} text-white`,
                'bg-[#7B3EF0] hover:bg-[#6b33e6] active:bg-[#5d2bc4]',
                'shadow-lg shadow-purple-600/20 hover:shadow-purple-600/30',
                'transition-colors duration-200'
              ].join(' ')}
            >
              {t('popularCountries.getStarted')}
            </button>
          </>
        ) : (
          <div className="py-8 text-center">
            <p className={`${COLORS.text.muted} ${TYPOGRAPHY.base}`}>
              {t('popularCountries.noPlansAvailable')}
            </p>
          </div>
        )}
      </div>

      {/* subtle hover ring */}
      <div className="pointer-events-none absolute inset-0 rounded-3xl ring-0 ring-purple-400/0 group-hover:ring-2 group-hover:ring-purple-400/40 transition" />
    </div>
  );
};


// Popular Countries Section with original UI + translations
const PopularCountriesSection = ({ user, setShowAuthModal, handleCountrySelect }) => {
  const { t } = useTranslation(); // Only add this line
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Popular country codes to display
  const popularCountryCodes = ['US', 'GB', 'FR', 'JP', 'ES', 'DE'];

  useEffect(() => {
    // Set up real-time listener for countries collection
    const unsubscribe = onSnapshot(
      collection(db, 'countries'),
      (snapshot) => {
        try {
          setError(null);
          const countriesData = [];
          
          snapshot.forEach((doc) => {
            const countryData = {
              iso: doc.id,
              ...doc.data()
            };
            
            // Only include popular countries
            if (popularCountryCodes.includes(countryData.iso)) {
              countriesData.push(countryData);
            }
          });

          // Sort by the order in popularCountryCodes
          countriesData.sort((a, b) => 
            popularCountryCodes.indexOf(a.iso) - popularCountryCodes.indexOf(b.iso)
          );

          console.log('Firebase countries data:', countriesData);
          setCountries(countriesData);
          setLoading(false);
        } catch (error) {
          console.error('Error processing Firebase data:', error);
          setError('Failed to process country data');
          setLoading(false);
        }
      },
      (error) => {
        console.error('Firebase listener error:', error);
        setError('Failed to load pricing data from Firebase');
        setLoading(false);
      }
    );

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, []);

  const handleBuyClick = (country) => {
    if (!user) {
      setShowAuthModal(true);
    } else {
      // Redirect to browse page with country selected
      window.location.href = `/browse?country=${country.iso}`;
    }
  };

  const handleRetryLoad = () => {
    setLoading(true);
    setError(null);
    // The onSnapshot listener will automatically retry
  };

  return (
    <div className={`py-20 lg:py-32 sm:px-32 ${COLORS.background.primary}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16 space-y-4">
          <h2 className={`${TYPOGRAPHY['4xl']} lg:text-5xl font-bold ${COLORS.text.primary}`}>
            {t('popularCountries.title')}
          </h2>
          <p className={`${TYPOGRAPHY.xl} ${COLORS.text.secondary} max-w-2xl mx-auto`}>
            {t('popularCountries.subtitle')}
          </p>
          
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg max-w-md mx-auto">
              <p className={TYPOGRAPHY.sm}>{error}</p>
              <button 
                onClick={handleRetryLoad}
                className={`mt-2 ${TYPOGRAPHY.sm} underline hover:no-underline`}
              >
                {t('common.tryAgain')}
              </button>
            </div>
          )}
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {loading ? (
            // Show loading skeletons
            Array.from({ length: 6 }).map((_, index) => (
              <CountryPriceCard key={index} loading={true} />
            ))
          ) : countries.length > 0 ? (
            // Show actual country data
            countries.map((country) => (
              <CountryPriceCard
                handleCountrySelect={handleCountrySelect}
                key={country.iso}
                country={country}
                onBuyClick={handleBuyClick}
              />
            ))
          ) : (
            // Show fallback message
            <div className="col-span-full text-center py-16 space-y-4">
              <Globe className="w-16 h-16 text-gray-300 mx-auto" />
              <p className={`${COLORS.text.muted} ${TYPOGRAPHY.lg}`}>
                Unable to load country pricing at the moment.
              </p>
              <button 
                onClick={handleRetryLoad}
                className={`px-6 py-3 ${TYPOGRAPHY.base} font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200`}
              >
                {t('common.tryAgain')}
              </button>
            </div>
          )}
        </div>
        
        <div className="text-center mt-16">
          <a 
            href="/browse"
            className={`inline-flex items-center px-8 py-4 ${TYPOGRAPHY.base} font-semibold bg-[#7B3EF0] hover:bg-[#6b33e6] text-white rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl`}
          >
            {t('popularCountries.viewAllCountries')}
            <ChevronRight className="ml-2 w-5 h-5" />
          </a>
        </div>
      </div>
    </div>
  );
};

// How It Works Section with original UI + translations
const HowItWorksSection = () => {
  const { t } = useTranslation(); // Only add this line

  const steps = [
    {
      number: '01',
      title: t('howItWorks.step1Title'),
      description: t('howItWorks.step1Desc'),
      image: <img src={WorldWide} className="h-12 w-auto sm:h-14" alt="Choose destination" />,
    },
    {
      number: '02',
      title: t('howItWorks.step2Title'),
      description: t('howItWorks.step2Desc'),
      image: <img src={QRCode} className="h-12 w-auto sm:h-14" alt="Install eSIM" />,
    },
    {
      number: '03',
      title: t('howItWorks.step3Title'),
      description: t('howItWorks.step3Desc'),
      image: <img src={Internet} className="h-12 w-auto sm:h-14" alt="Connect automatically" />,
    },
  ];

  return (
    <section className="relative py-20 sm:px-32 lg:py-28 overflow-x-clip lg:overflow-visible">
      {/* soft background glow */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-purple-100/50 to-transparent" />
      <div className="pointer-events-none absolute -inset-x-24 -bottom-24 h-56 bg-gradient-to-t from-indigo-100/40 to-transparent blur-2xl" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        {/* header */}
        <div className="text-center mb-16 space-y-4">
          <h2 className={`${TYPOGRAPHY['4xl']} lg:text-5xl font-bold ${COLORS.text.primary}`}>
            {t('howItWorks.title')}
          </h2>
          <p className={`${TYPOGRAPHY.xl} ${COLORS.text.secondary} max-w-2xl mx-auto`}>
            {t('howItWorks.subtitle')}
          </p>
        </div>

        {/* steps */}
        <ol
          className="
            grid gap-10 lg:gap-12
            lg:grid-cols-3
          "
        >
          {steps.map((step, idx) => (
            <li
              key={idx}
              className="
                relative group
                rounded-3xl
                bg-white
                border border-gray-200/80
                shadow-sm hover:shadow-xl
                transition-all duration-300
                p-6 sm:p-7
              "
            >
              {/* connector line between cards (desktop) */}
              {idx < steps.length - 1 && (
                <span
                  aria-hidden="true"
                  className="
                    hidden lg:block
                    absolute top-1/2 right-0 translate-x-1/2 -translate-y-1/2
                    h-[2px] w-16
                    bg-gradient-to-r from-purple-400/40 via-purple-500/60 to-indigo-500/40
                  "
                />
              )}

              {/* badge row */}
              <div className="flex items-center justify-between">
                {/* number badge */}
                <span
                  className="
                    inline-flex items-center justify-center
                    h-10 w-10 rounded-2xl
                    bg-white/70 backdrop-blur
                    ring-1 ring-gray-200
                    text-gray-900 text-sm font-bold
                    shadow-sm
                  "
                >
                  {step.number}
                </span>

                {/* tiny "glass" chip that says Step */}
                <span
                  className="
                    hidden sm:inline-flex items-center px-2.5 py-1 rounded-full
                    text-xs font-medium
                    bg-purple-50 text-purple-700 ring-1 ring-purple-200
                  "
                >
                  Step {idx + 1}
                </span>
              </div>

              {/* icon */}
              <div className="mt-6">
                <div
                  className="
                    inline-flex items-center justify-center
                    h-20 w-20 rounded-2xl
                    bg-gradient-to-br from-purple-50 to-indigo-50
                    ring-1 ring-gray-100
                    shadow-inner
                  "
                >
                  {step.image}
                </div>
              </div>

              {/* title & copy */}
              <h3
                className={`
                  mt-6 ${TYPOGRAPHY['2xl']} font-semibold ${COLORS.text.primary}
                  tracking-tight
                `}
              >
                {step.title}
              </h3>
              <p
                className={`${TYPOGRAPHY.base} ${COLORS.text.secondary} leading-relaxed mt-3 max-w-sm`}
              >
                {step.description}
              </p>

              {/* CTA cue */}
              <div className="mt-5">
                <div
                  className="
                    inline-flex items-center gap-2 text-sm font-semibold
                    text-purple-700 group-hover:text-purple-800
                    transition-colors
                  "
                >
                  {t('howItWorks.learnMore')}
                  <span className="transition-transform duration-300 group-hover:translate-x-0.5">
                    →
                  </span>
                </div>
              </div>

              {/* hover ring */}
              <div className="pointer-events-none absolute inset-0 rounded-3xl ring-0 ring-purple-400/0 group-hover:ring-2 group-hover:ring-purple-400/30 transition" />
            </li>
          ))}
        </ol>

        {/* mobile timeline connector (between stacked cards) */}
        <div className="lg:hidden mt-10 grid grid-cols-3 gap-6 opacity-70">
          {[0, 1].map((i) => (
            <div key={i} className="col-span-3 flex items-center justify-center">
              <span className="h-[2px] w-24 bg-gradient-to-r from-purple-300 via-purple-500 to-indigo-400" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Benefits Section with original UI + translations
const BenefitsSection = () => {
  const { t } = useTranslation(); // Only add this line

  const benefits = [
    {
      icon: <Layers size={20} />,
      title: t('benefits.item1Title'),
      description: t('benefits.item1Desc'),
    },
    {
      icon: <CheckCircle size={20} />,
      title: t('benefits.item2Title'),
      description: t('benefits.item2Desc'),
    },
    {
      icon: <FileText size={20} />,
      title: t('benefits.item3Title'),
      description: t('benefits.item3Desc'),
    },
    {
      icon: <Smartphone size={20} />,
      title: t('benefits.item4Title'),
      description: t('benefits.item4Desc'),
    },
    {
      icon: <Bell size={20} />,
      title: t('benefits.item5Title'),
      description: t('benefits.item5Desc'),
    },
    {
      icon: <Globe size={20} />,
      title: t('benefits.item6Title'),
      description: t('benefits.item6Desc'),
    },
  ];

  return (
    <section className={`relative py-20 sm:px-32 lg:py-28 ${COLORS.background.primary}`}>
      {/* soft ambient glow */}
      <div className="pointer-events-none absolute inset-x-0 -top-16 h-24 bg-gradient-to-b from-purple-100/50 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 -bottom-16 h-24 bg-gradient-to-t from-indigo-100/40 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        {/* header */}
        <div className="mb-12 space-y-3">
          <p className={`${COLORS.text.muted} ${TYPOGRAPHY.sm} font-medium`}>{t('benefits.whyAirvoy')}</p>
          <h2 className={`${TYPOGRAPHY['4xl']} lg:text-5xl font-bold ${COLORS.text.primary} max-w-3xl tracking-tight`}>
            {t('benefits.title')}
          </h2>
        </div>

        {/* benefit cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {benefits.map((b, i) => (
            <div
              key={i}
              className={[
                'group relative rounded-3xl',
                'bg-white/70 backdrop-blur supports-[backdrop-filter]:bg-white/70',
                'border border-gray-200/80 shadow-sm',
                'transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5',
                'focus-within:ring-2 focus-within:ring-purple-400'
              ].join(' ')}
            >
              {/* top accent line */}
              <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-purple-400 via-fuchsia-500 to-indigo-500 opacity-70" />

              <div className="p-6 lg:p-7">
                <div className="flex items-start gap-4">
                  {/* gradient icon chip */}
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl ring-1 ring-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50 text-purple-700">
                    {b.icon}
                  </span>

                  <div className="flex-1">
                    <h3 className={`${TYPOGRAPHY.xl} font-semibold ${COLORS.text.primary} tracking-tight`}>
                      {b.title}
                    </h3>
                    <p className={`${TYPOGRAPHY.base} ${COLORS.text.secondary} leading-relaxed mt-2`}>
                      {b.description}
                    </p>
                  </div>
                </div>
              </div>

              {/* hover ring */}
              <div className="pointer-events-none absolute inset-0 rounded-3xl ring-0 ring-purple-400/0 group-hover:ring-2 group-hover:ring-purple-400/30 transition" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
 
// Protected Route Component with original UI + translations
const ProtectedRoute = ({ children, user, setShowAuthModal }) => {
  const { t } = useTranslation();
 
  if (!user) {
    return (
      <div className={`min-h-screen ${COLORS.background.secondary} flex items-center justify-center`}>
        <div className="text-center space-y-6 max-w-md mx-auto px-6">
          <Shield className="w-16 h-16 text-blue-600 mx-auto" />
          <h2 className={`${TYPOGRAPHY['2xl']} font-bold ${COLORS.text.primary}`}>
            {t('aut.authenticationRequired')}
          </h2>
          <p className={`${TYPOGRAPHY.base} ${COLORS.text.secondary}`}>
            {t('aut.signInPrompt')} 
          </p>
          <button
            onClick={() => setShowAuthModal(true)}
            className={`px-8 py-3 ${TYPOGRAPHY.base} font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200`}
          >
            {t('nav.signIn')}
          </button>
        </div>
      </div>
    );
  }
  
  return children;
};

// HowToVideosSection with original UI + translations
const HowToVideosSection = () => {
  const { t } = useTranslation();

  const cards = [
    {
      // iPhone
      src: "https://maresone.s3.us-east-1.amazonaws.com/airvoy/iphone.mp4",
      title: t('videos.iPhoneTitle'),
      blurb: t('videos.iPhoneDesc'),
      badge: (
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 ring-1 ring-purple-200">
          <img src={AppleLogo} alt="" className="h-4 w-4" />
          {t('videos.iOSGuide')}
        </span>
      ),
      reverseOnDesktop: false, // video left, text right
    },
    {
      // Android / Samsung
      src: "https://maresone.s3.us-east-1.amazonaws.com/airvoy/android.mp4",
      title: t('videos.samsungTitle'),
      blurb: t('videos.samsungDesc'),
      badge: (
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200">
          <img src={AndroidLogo} alt="" className="h-4 w-4" />
          {t('videos.samsungGuide')}
        </span>
      ),
      reverseOnDesktop: true, // video right, text left
    },
  ];

  return (
    <section className="relative py-20 sm:px-32 lg:py-28 bg-white">
      {/* ambient glow */}
      <div className="pointer-events-none absolute inset-x-0 -top-16 h-24 bg-gradient-to-b from-purple-100/50 to-transparent" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        {/* header */}
        <div className="text-center mb-14 space-y-3">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 tracking-tight">
            {t('videos.title')}
          </h2>
          <p className="text-gray-600 text-lg">
            {t('videos.subtitle')}
          </p>
        </div>

        <div className="space-y-12 lg:space-y-14">
          {cards.map((c, idx) => (
            <div
              key={idx}
              className={[
                "grid items-center gap-8 lg:gap-12",
                c.reverseOnDesktop ? "lg:grid-cols-2 lg:[&>*:first-child]:order-2" : "lg:grid-cols-2",
              ].join(" ")}
            >
              {/* video frame */}
              <div className="relative overflow-hidden rounded-3xl border border-gray-200/80 shadow-sm transition hover:shadow-xl">
                {/* subtle gradient frame */}
                <div className="absolute inset-0 rounded-3xl pointer-events-none ring-1 ring-inset ring-white/60" />
                <div className="absolute -inset-px rounded-3xl bg-[conic-gradient(from_180deg_at_50%_50%,rgba(123,62,240,.18),transparent_40%)]" />
                {/* aspect ratio 16:9 */}
                <div className="relative w-full rounded-[inherit]" style={{ paddingTop: "56.25%" }}>
                  <video
                    className="absolute inset-0 h-full w-full rounded-[inherit]"
                    src={c.src}
                    controls
                    playsInline
                    preload="metadata"
                  />
                </div>
              </div>

              {/* text block */}
              <div className="relative">
                <div className="mb-3">{c.badge}</div>
                <h3 className="text-2xl lg:text-3xl font-semibold text-gray-900 tracking-tight">
                  {c.title}
                </h3>
                <p className="text-gray-600 text-base leading-relaxed mt-3 max-w-prose">
                  {c.blurb}
                </p>

                {/* single, refined CTA */}
                <div className="mt-6">
                  <Link
                    to="/installation"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-purple-800 hover:text-purple-900"
                  >
                    <span className="inline-flex h-2 w-2 rounded-full bg-purple-500" />
                    {t('videos.installationGuide')}
                  </Link>
                </div>

                {/* divider */}
                <div className="mt-8 h-px w-full bg-gradient-to-r from-gray-200 via-gray-100 to-transparent" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};


// Home Page Component
const HomePage = ({ user, handleCountrySelect, setShowAuthModal }) => (
  <div className=''>
    <HeroSection />
    <PopularCountriesSection handleCountrySelect={handleCountrySelect} user={user} setShowAuthModal={setShowAuthModal} />
    <RegionalPlansSection />
    <HowItWorksSection />
    <BenefitsSection />
    <AirvoyActivateAndHow/>
    <HowToVideosSection />
  </div>
);

// Footer with original UI + translations
const Footer = () => {
  const { t } = useTranslation(); // Only add this line
 
  return (
    <footer className={`bg-white pt-16 sm:px-32 pb-8 relative overflow-hidden`}>
      {/* Decorative gradient line */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-600 via-purple-500 to-pink-500"></div>
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid md:grid-cols-5 gap-12">
          {/* Brand */}
          <div className="md:col-span-2 space-y-6"> 
           <Link to="/" className="flex items-center gap-3">
            <img
              src={LogoClear} 
              alt="Airvoy Logo"
              className="h-9 w-auto"
            />
          </Link>
            <p className={`${TYPOGRAPHY.base} ${COLORS.text.secondary} leading-relaxed max-w-md`}>
              {t('footer.description')}
            </p>

            {/* Store links */}
            <div className="flex items-center gap-4">
              <a
                href="https://play.google.com/store/apps/details?id=com.airvoy.airvoy"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get Airvoy on Google Play"
              >
                <img
                  src={AndroidLogo}
                  alt="Android"
                  className="h-12 w-auto hover:opacity-80 transition"
                  loading="lazy"
                  decoding="async"
                />
              </a>
              <a
                href="https://apps.apple.com/us/app/airvoy-esim/id6751522296"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get Airvoy on Apple Store"
              >
                <img
                  src={AppleLogo}
                  alt="iOS"
                  className="h-12 w-auto hover:opacity-80 transition"
                  loading="lazy"
                  decoding="async"
                />
              </a>
            </div>

            {/* PCI DSS Compliance Badge */}
            <div className="pt-2">
              <img
                src={PCIDSS}
                alt="PCI DSS Compliant"
                className="h-16 w-auto"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
          
          {/* Company */}
          <div className="space-y-4">
            <h4 className={`${TYPOGRAPHY.lg} font-semibold ${COLORS.text.primary}`}>{t('footer.company')}</h4>
            <ul className="space-y-3">
              {[
                { href: "/about-us", label: t('footer.aboutUs') },
                { href: "/support", label: t('footer.support') },
                { href: "/faq", label: t('nav.faqs') },
              ].map((link) => (
                <li key={link.href}>
                  <a 
                    href={link.href} 
                    className={`${TYPOGRAPHY.base} ${COLORS.text.secondary} hover:text-blue-600 transition-colors duration-200`}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Legal */}
          <div className="space-y-4">
            <h4 className={`${TYPOGRAPHY.lg} font-semibold ${COLORS.text.primary}`}>{t('footer.resources')}</h4>
            <ul className="space-y-3">
              {[
                { href: "/privacy", label: t('footer.privacyPolicy') },
                { href: "/terms-of-service", label: t('footer.termsOfService') },
                { href: "/documentation", label: t('footer.documentation') },
              ].map((link) => (
                <li key={link.href}>
                  <a 
                    href={link.href} 
                    className={`${TYPOGRAPHY.base} ${COLORS.text.secondary} hover:text-blue-600 transition-colors duration-200`}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Social Media */}
          <div className="space-y-4">
            <h4 className={`${TYPOGRAPHY.lg} font-semibold ${COLORS.text.primary}`}>{t('footer.followUs')}</h4>
            <ul className="space-y-3">
              <li className="flex items-center space-x-4">
                <a 
                  className="flex items-center transition-colors duration-200" 
                  href="https://www.tiktok.com/@airvoy_esim" 
                  target="_blank" 
                  rel="noopener noreferrer"
                > 
                  <FaTiktok className="w-5 h-5" />
                </a>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Bottom bar */}
        <div className="border-t border-gray-300 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
          <p className={`${TYPOGRAPHY.sm} ${COLORS.text.muted} text-center md:text-left`}>
            &copy; {new Date().getFullYear()} Routed Labs, Inc. {t('footer.copyright')}
          </p>
          <p className={`${TYPOGRAPHY.sm} ${COLORS.text.muted} text-center md:text-right`}>
            {t('footer.madeWith')} <span className="text-red-500">♥</span> {t('footer.forTravelers')}
          </p>
        </div>
      </div>
    </footer>
  );
};

function App() { 
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    (function (c, l, a, r, i, t, y) {
      c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments) };
      t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
      y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
    })(window, document, "clarity", "script", "t8p9j79jha");
  }, []);

  const handleCountrySelect = (country) => {
    window.location.href = `/country/${country.iso}`;
  };

  // Add custom styles for text shadow
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .shadow-text {
        text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  if (loading) {
    return (
      <div className={`min-h-screen ${COLORS.background.accent} flex items-center justify-center`}>
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className={`${COLORS.text.secondary} ${TYPOGRAPHY.base}`}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <LanguageProvider>
    <ThemeProvider>
      <Router>
        <ScrollToTop />
        <div className={`min-h-screen ${COLORS.background.primary} transition-colors duration-200`}>
          <Navigation 
            user={user} 
            showAuthModal={showAuthModal} 
            setShowAuthModal={setShowAuthModal} 
          />
          
          <Routes>
            <Route 
              path="/" 
              element={
                <HomePage 
                  handleCountrySelect={handleCountrySelect}
                  user={user} 
                  setShowAuthModal={setShowAuthModal} 
                />
              } 
            />
            <Route 
              path="/country/:countryCode" 
              element={
                <CountryDetails 
                  user={user} 
                  setShowAuthModal={setShowAuthModal}
                />
              } 
            />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route
              path="/regional/:regionSlug"
              element={
                <RegionalPlansPage
                  user={user}
                  setShowAuthModal={setShowAuthModal}
                />
              }
            />
            <Route
              path="/startups-application"
              element={
                <Startups
                  user={user}
                  setShowAuthModal={setShowAuthModal}
                />
              }
            />
            <Route 
              path="/browse" 
              element={
                <Browse 
                  user={user} 
                  setShowAuthModal={setShowAuthModal}
                />
              } 
            />
            <Route  
              path="/post/:slug" 
              element={
                <Post 
                  user={user} 
                  setShowAuthModal={setShowAuthModal}
                />
              } 
            />
            <Route 
              path="/partnerships" 
              element={
                <Partnerships 
                  user={user} 
                  setShowAuthModal={setShowAuthModal}
                />
              } 
            />
            <Route 
              path="/privacy-policy" 
              element={
                <PrivacyPolicy 
                  user={user} 
                  setShowAuthModal={setShowAuthModal}
                />
              } 
            />
            <Route 
              path="/documentation" 
              element={
                <AirvoyAPIDocs 
                  user={user} 
                  setShowAuthModal={setShowAuthModal}
                />
              } 
            />
            <Route 
              path="/terms-of-service" 
              element={
                <TermsOfService 
                  user={user} 
                  setShowAuthModal={setShowAuthModal}
                />
              } 
            />
            <Route 
              path="/support" 
              element={
                <Support 
                  user={user} 
                  setShowAuthModal={setShowAuthModal}
                />
              } 
            />
            <Route 
              path="/about-us" 
              element={
                <AboutUs 
                  user={user} 
                  setShowAuthModal={setShowAuthModal}
                />
              } 
            />
            <Route  
              path="/installation-android-and-ios" 
              element={<PlatformInstallation />} 
              />
            <Route 
              path="/installation" 
              element={
                <Installation 
                  user={user} 
                  setShowAuthModal={setShowAuthModal}
                />
              } 
            />
            <Route 
              path="/faq" 
              element={
                <Faq 
                  user={user} 
                  setShowAuthModal={setShowAuthModal}
                />
              } 
            /> 
            <Route 
              path="/ios-guide" 
              element={
                <IOSGuide 
                  user={user} 
                  setShowAuthModal={setShowAuthModal}
                />
              } 
            /> 
            <Route 
              path="/android-guide" 
              element={
                <AndroidGuide 
                  user={user} 
                  setShowAuthModal={setShowAuthModal}
                />
              } 
            /> 
            <Route 
              path="/additional-questions" 
              element={
                <Additional 
                  user={user} 
                  setShowAuthModal={setShowAuthModal}
                />
              } 
            />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute 
                  user={user} 
                  showAuthModal={showAuthModal}
                  setShowAuthModal={setShowAuthModal}
                > 
                  <Profile 
                    user={user} 
                    showAuthModal={showAuthModal}
                    setShowAuthModal={setShowAuthModal}
                  />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute 
                  user={user} 
                  showAuthModal={showAuthModal}
                  setShowAuthModal={setShowAuthModal}
                >
                  <Dashboard 
                    user={user} 
                    showAuthModal={showAuthModal}
                    setShowAuthModal={setShowAuthModal}
                  />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute 
                  user={user} 
                  showAuthModal={showAuthModal}
                  setShowAuthModal={setShowAuthModal}
                >
                  <BusinessAdmin 
                    user={user} 
                    showAuthModal={showAuthModal}
                    setShowAuthModal={setShowAuthModal}
                  />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/checkout" 
              element={
                <Checkout 
                  user={user} 
                  setShowAuthModal={setShowAuthModal}
                />
              } 
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          
          <Footer />
          
          {showAuthModal && (
            <AuthModal 
              isOpen={showAuthModal}
              onClose={() => setShowAuthModal(false)}
            />
          )}
        </div>
      </Router>
    </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;